import {
  Avatar as AvatarUi,
  AvatarStatus,
  Skeleton,
  Theme,
  Tooltip,
  getAvatarSize,
} from 'team-hero-ui';
import { ReactElement, useMemo } from 'react';

import type { IAvatarProps } from './Avatar.types';
import useGetImageUrl from 'hooks/useGetImageUrl.hook';

const Avatar = <T,>({
  images,
  size,
  sourceType,
  additionalRefreshTrigger,
  showStatus = false,
  status = 'default',
  firstName,
  lastName,
  embeddedContact,
  color,
  bgColor,
  onClick,
  thumbnailSize,
  icon,
  showSkeleton,
  onMaximizeClick,
  displayHoverOverlay = false,
}: IAvatarProps<T>): ReactElement => {
  const { imageUrls, isLoading } = useGetImageUrl({
    images,
    imageType: sourceType,
    additionalRefreshTrigger,
    thumbnailSize,
  });

  const getFirstName = useMemo((): string | null => {
    if (firstName) {
      return firstName;
    }
    if (embeddedContact) {
      return embeddedContact.firstName;
    }
    return null;
  }, [embeddedContact, firstName]);

  const getLastName = useMemo((): string | null => {
    if (lastName) {
      return lastName;
    }
    if (embeddedContact) {
      return embeddedContact.lastName;
    }
    return null;
  }, [embeddedContact, lastName]);

  if (showSkeleton || (isLoading && !getFirstName && !getLastName)) {
    return (
      <Skeleton.Avatar
        width={getAvatarSize(size) || 30}
        height={getAvatarSize(size) || 30}
      />
    );
  }

  const url = Array.isArray(imageUrls) ? imageUrls[0] : imageUrls;

  return showStatus ? (
    <AvatarStatus
      size={size}
      image={url}
      status={status}
      color={color}
      firstName={getFirstName || undefined}
      lastName={getLastName || undefined}
      avatarOnClick={onClick}
      bgColor={
        embeddedContact?.isExternal ? Theme.colors.primary.grey : bgColor
      }
    />
  ) : (
    <Tooltip
      title={embeddedContact?.provider?.name || ''}
      styleType='top'
      bgColor={Theme.colors.primary.grey}
      textColor={Theme.colors.primary.white}
    >
      <div>
        <AvatarUi
          firstName={getFirstName || undefined}
          lastName={getLastName || undefined}
          size={size}
          images={imageUrls}
          avatarOnClick={onClick}
          bgColor={
            embeddedContact?.isExternal ? Theme.colors.primary.grey : bgColor
          }
          displayHoverOverlay={displayHoverOverlay}
          onMaximizeClick={onMaximizeClick}
          icon={icon}
        />
      </div>
    </Tooltip>
  );
};

export default Avatar;
